import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import accountingReportService, { accountingEndpoint, accountingAttachFormats } from '@api/v1/accountingReportService'
import reportJobService from '@api/v1/reportJobService'
import useReadState from '@hooks/useReadState'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import {
  multiObjectField, dateRangeField, choiceField, listField,
} from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import CsvReportGrid from '@components/reports/CsvReportGrid'
// CC-50162: CB caps this report's window at five years (stored-proc RAISERROR).
import { rangeTooLong, rangeTooLongMessage } from './matterMetricsRange'

// CBObjectTypes (V1's MatterMetricReportHomeView filter wiring).
const TYPE_USERS = 1
const TYPE_COMPANIES = 3
const TYPE_MATTERS = 1001

// "id:name|id2:name2" (the TagManagerFilter storage format) → V1's wire
// format: ids pipe-joined WITH a trailing pipe; '' when nothing is selected.
const pipeIds = (v) => {
  const ids = String(v || '')
    .split('|')
    .filter(Boolean)
    .map((p) => (p.includes(':') ? p.slice(0, p.indexOf(':')) : p))
  return ids.length > 0 ? `${ids.join('|')}|` : ''
}

// Practice Area follows V1 exactly: Select-All → the '$$cb_all' sentinel
// (Get_MatterMetricReportFilter handles it), names pipe-joined otherwise.
const practiceAreaWire = (v) => {
  const s = String(v || '')
  if (!s) return ''
  if (s === '$$cb_all' || s.toUpperCase() === 'ALL') return '$$cb_all'
  const names = s.split('|').filter(Boolean)
  return names.length > 0 ? `${names.join('|')}|` : ''
}

/**
 * Matter Metrics (report 168) — a period matrix: one row per matter (or
 * timekeeper) and, per period, an Actual/Budget pair for each configured metric.
 *
 * CC-50238: this used to call MatterMetric/ReportFilter then GetPageOfData and
 * rendered nothing at all, because ReportFilter answers {RowCount, ResultsId}
 * while the page read `totalCount` — so it short-circuited to "No data" on every
 * run, from its first commit onwards. Two more mismatches sat behind that:
 * ReportFilter returns no column set (V1 builds one from per-tenant grid
 * settings), and GetPageOfData returns positional cells, not keyed rows.
 *
 * Rather than reimplement V1's pivot builder, this now reads CB's OWN export of
 * the same report (DownloadMatterMetricReport) through cb-api, which parses the
 * CSV into { headers, rows, meta }. CB therefore owns the period expansion, the
 * Actual/Budget layout and the Totals row, so they cannot drift from V1.
 */
const makeDefaults = () => ({
  startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
  budgetType: 'matter', // 'matter' | 'timekeeper' — V1's Budget Type radio
  matters: '',          // TagManagerFilter values ("id:name|id2:name2")
  clients: '',
  attorneys: '',        // Managing Partner (Responsible Attorney)
  timekeepers: '',
  practiceAreas: '',    // multi-select ('A|B', '$$cb_all', or '')
  budgetStatus: '0',    // 0 All / 1 Has Budget / 2 Missing Budget (V1 select)
})

/**
 * Built per render: the field set DEPENDS on Budget Type (matter mode exposes
 * Matter/Client/Managing Partner/Practice Area, timekeeper mode exposes only
 * Timekeeper), and Practice Area's options are server-loaded.
 *
 * The multi pickers keep their raw "id:name|…" widget strings in state;
 * `pipeIds()` / `practiceAreaWire()` in runReport remain the only wire encoders,
 * so V1's trailing-pipe and `$$cb_all` conventions are untouched.
 *
 * `omitAll` on the date range: a start date is REQUIRED here (without one the
 * report used to post MinDate 1776-07-04 and CB answered 500), so offering
 * "All Dates" would advertise a run that cannot work.
 */
const buildFields = (isMatterMode, practiceAreaOptions) => [
  dateRangeField({ key: 'dates', label: 'Date Range', startKey: 'startDate', endKey: 'endDate', omitAll: true }),
  ...(isMatterMode
    ? [
        multiObjectField({ key: 'matters', label: 'Matter', typeId: TYPE_MATTERS, valueKey: 'matters' }),
        multiObjectField({ key: 'clients', label: 'Client', typeId: TYPE_COMPANIES, valueKey: 'clients' }),
        multiObjectField({ key: 'attorneys', label: 'Managing Partner', typeId: TYPE_USERS, valueKey: 'attorneys' }),
        listField({
          key: 'practiceAreas', label: 'Practice Area', valueKey: 'practiceAreas',
          options: practiceAreaOptions.map((a) => ({ value: a, label: a })),
        }),
      ]
    : [multiObjectField({ key: 'timekeepers', label: 'Timekeeper', typeId: TYPE_USERS, valueKey: 'timekeepers' })]),
  choiceField({
    key: 'budgetStatus', label: 'Budget Status', valueKey: 'budgetStatus',
    options: [
      { value: '0', label: 'All' },
      { value: '1', label: 'Has Budget' },
      { value: '2', label: 'Missing Budget' },
    ],
  }),
]


/**
 * The wire boundary, lifted verbatim from runReport. V1 conventions are
 * load-bearing: pipe-joined ids WITH a trailing pipe, and $$cb_all for
 * select-all practice areas. The client/attorney/practice-area filters apply
 * only in matter mode.
 */
const toWire = (filters) => {
  const isMatterMode = filters.budgetType !== 'timekeeper'
  return {
      objectType: isMatterMode ? TYPE_MATTERS : TYPE_USERS,
      // CC-49900: NO 1776 fallback — an empty start date used to default to
      // 1776-07-04 and crash the report with no user error involved. A start
      // date is now required (guarded above + the Run button is disabled
      // without one), so this is always a real user-chosen date.
      //
      // CC-50162 CORRECTION to the note that stood here: this was described as
      // CB "500ing on an early MinDate (1776/1900/2020 fail, 2024 works)".
      // That reads the symptom, not the rule. The proc rejects on RANGE
      // LENGTH — `@maxDate > DATEADD(YEAR, 5, @minDate)` — so those early
      // starts failed only because MaxDate was left at today. A five-year
      // window starting in 1900 is accepted; a six-year one starting in 2020
      // is not. See ./matterMetricsRange.js. (1776 did fail either way, which
      // is what made the wrong rule look confirmed.)
      minDate: filters.startDate,
      maxDate: filters.endDate || '9999-12-31',
      budgetStatusFilter: parseInt(filters.budgetStatus, 10) || 0,
      relatedIds: pipeIds(isMatterMode ? filters.matters : filters.timekeepers),
      clientIds: isMatterMode ? pipeIds(filters.clients) : '',
      userLookupIds: isMatterMode ? pipeIds(filters.attorneys) : '',
      practiceAreaFilter: isMatterMode ? practiceAreaWire(filters.practiceAreas) : '',
      // V1 shows this column only when the tenant has configured a projected
      // fees field AND the window runs past the current period
      // (MatterMetricReportHomeView.js:156). cb2 has no reader for those grid
      // settings yet, so it stays off — see the DEFERRED note on CC-50238.
      displayProjectedFeesCol: false,
    }
}

// CC-50148: reportId/jobId were always PASSED by ReportView; this page just never
// declared them, which is why it had no queue control and no way to reopen a saved
// run. The queue spec is withheld without a reportId (a job record requires one).
// CC-50162: prefer CB's own explanation. cb-api forwards the (bounded)
// ErrorMessage as `error` on the failure body; axios's own err.message is only
// ever "Request failed with status code 500", which is what QA had to file a
// ticket to interpret. Module-level so useLatestRun's `run` keeps one identity.
const runErrorMessage = (err) => err.response?.data?.error || err.message || 'Report failed'

const MatterMetricsReport = ({ reportId, name = 'Matter Metrics', summary, jobId }) => {
  /*
   * CC-53132 — a permission read that never happened is not permission.
   *
   * This defaulted to `true`, so a paused or failed can-access read read as "you
   * may view Matter Metrics" and the report ran regardless. `isLoading` cannot
   * catch it: it is `isPending && isFetching`, so it is FALSE for a PAUSED query
   * (default `networkMode: 'online'` while `onlineManager` believes the browser
   * is offline) — `queryFn` is never called and nothing is logged on either side.
   *
   * Failing open costs something real here. CB enforces this permission itself,
   * and on the CSV-backed path it answers a refusal as a human-readable SENTENCE
   * where the header row belongs — which the parser then renders as a one-column
   * report named after the error (CC-50312). So the run now waits for the read,
   * and an unresolved read is reported as neither a yes nor a no.
   */
  const canAccessQuery = useQuery({
    queryKey: ['matter-metrics-can-access'],
    queryFn: () => accountingReportService.matterMetrics.canAccess(),
    staleTime: 5 * 60_000,
  })
  const canAccessState = useReadState('matter-metrics-can-access', canAccessQuery)
  const canAccessReady = canAccessState === 'ready'
  // No `?? true`: only a completed read may answer this question either way.
  const canAccess = canAccessQuery.data === true
  /*
   * "Not ready and not on its way" — blocked, failed, OR idle.
   *
   * `idle` is folded in rather than named, and that is the whole point. It means
   * pending-but-never-started (`enabled: false`), which this query cannot reach
   * today because it has no `enabled` option. But the run below is now WITHHELD
   * until the read resolves, so whoever adds an `enabled` gate later would get a
   * report that sits un-run forever with nothing on screen to say why — the exact
   * trap. Anything that is not ready and not in flight gets the panel.
   */
  const canAccessUnresolved = canAccessState !== 'ready' && canAccessState !== 'loading'

  // CC-49900: V1's full filter set — Budget Type (matter/timekeeper), the four
  // lookups, Practice Area, and Budget Status — on top of the date range.
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria — Budget Type re-runs and is never disabled while a
  // run is out. Spinner immediately when reopening a saved result.
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.startDate, filters.endDate)), [filters])
  /*
   * CC-50238: { headers, rows, meta } straight from cb-api's CSV parse.
   *
   * CC-53132: `null` until a run RESOLVES — not an object pre-filled with empty
   * arrays. `CsvReportGrid` coerces `data?.rows || []` internally and hands
   * `DataGrid` a real array either way, so `DataGrid`'s own unresolved-read guard
   * (`Array.isArray`) can NEVER fire from here — an empty-but-shaped object made
   * it print "No data for the selected filters" and a row count of 0 for a run
   * that had not happened, or was refused. Gated at the render site below instead.
   */
  const [data, setData] = useState(null)

  // Practice Area options = the same list V1's page loads (field 1001:1054).
  const practiceAreasQuery = useQuery({
    queryKey: ['matter-metrics-practice-areas'],
    queryFn: () => accountingReportService.matterMetrics.practiceAreas(),
    staleTime: 5 * 60_000,
  })
  const practiceAreasState = useReadState('matter-metrics-practice-areas', practiceAreasQuery)
  // Same "not ready and not on its way" rule, for the same reason.
  const practiceAreasUnresolved = practiceAreasState !== 'ready' && practiceAreasState !== 'loading'
  /*
   * Coerced ONLY to build the picker's option list. What must not happen is the
   * empty list reading as "your firm has no practice areas": per `toWire`, an
   * unselected Practice Area encodes to '' via `practiceAreaWire`, and '' is
   * scope-WIDENING on this report — CB reads it as every practice area. So a
   * picker that failed to load looks identical to a filter the user chose to
   * leave off, while the report quietly covers the whole firm. The panel below
   * is what makes the two distinguishable.
   *
   * Memoized because it is a dependency of the `fields` memo — a fresh `[]` every
   * render is a ticking value reaching a columns/fields memo, which this repo has
   * been bitten by twice. (The destructuring default it replaces had the same
   * flaw, just invisibly.)
   */
  const practiceAreaOptions = useMemo(() => practiceAreasQuery.data ?? [], [practiceAreasQuery.data])

  const runReport = useCallback(() => {
    // Order matters: "we could not check" and "you may not" are different facts,
    // and only one of them is the user's to fix. Saying the second when the
    // first is true accuses the user of lacking a permission they may well have.
    //
    // Spec 131: every refusal below also RETIRES a run still in flight, so an
    // earlier run's matrix cannot land under the refusal message.
    if (!canAccessReady) {
      invalidateRun({
        error:
          'We couldn’t confirm your access to Matter Metrics, so the report has not been run. ' +
          'This is not a refusal — the check itself never completed. ' +
          'Check your connection and try again.',
      })
      return undefined
    }
    if (!canAccess) {
      invalidateRun({ error: 'You do not have permission to view Matter Metrics.' })
      return undefined
    }
    // CC-49900: a start date is REQUIRED. Without one the report used to post
    // MinDate 1776-07-04, which CB answers with a 500 (see the wire comment
    // below) — so a cleared date read to the user as "the report is broken".
    if (!filters.startDate) {
      invalidateRun({ error: 'Select a start date to run this report.' })
      return undefined
    }
    // CC-50162: catch the 5-year cap before the round-trip. QA reported this as
    // "API 500 for early date ranges (≤2020)", but the start date is
    // irrelevant — 2020-01-01 → 2020-12-31 returns 200 with 530 rows. What
    // fails is the SPAN: any window longer than five years. Leaving the end
    // date at today is what made early starts look cursed.
    if (rangeTooLong(filters.startDate, filters.endDate)) {
      invalidateRun({ error: rangeTooLongMessage() })
      return undefined
    }
    return startRun(async () => {
      // MatterMetricReportFilter — V1's exact shape (MatterMetricReportHomeView
      // setupFilterData): ObjectType picks the budget type, RelatedIds carries
      // matters (matter mode) or timekeepers (timekeeper mode), and the
      // client/attorney/practice-area filters only apply in matter mode.
      //
      // CC-50238: these are now query params on DownloadMatterMetricReport
      // rather than a posted filter object. Same field names, same V1 wire
      // formats (pipe-joined ids with a trailing pipe, '$$cb_all' for
      // select-all practice areas) — only the transport changed.
      const params = toWire(filters)
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        // CC-53132: a saved result we cannot READ is not a saved report with no
        // rows in it. It used to fall back to empty arrays, which then rendered
        // an empty matrix under the tray's dated "saved result" banner — i.e. it
        // attributed the emptiness to CB's answer at that timestamp. Spec 131:
        // the banner is now stamped in the commit, AFTER this check, so an
        // unreadable snapshot no longer gets a dated banner over no data at all.
        const saved = r?.data
        if (!saved || !Array.isArray(saved.headers) || !Array.isArray(saved.rows)) {
          throw new Error('That saved result could not be read. Run the report again to rebuild it.')
        }
        return () => { setSavedAsOf(st?.createdAt || null); setData(saved) }
      }
      const fresh = await accountingReportService.matterMetrics.report(params)
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setData(fresh) }
    }, { criteria: { filtersSummary } })
  }, [filters, canAccess, canAccessReady, startRun, invalidateRun, filtersSummary])

  // Re-run on mount AND when the Budget Type flips — matter vs timekeeper
  // return different column sets, so stale rows under the other mode's labels
  // would misread (same hazard as AR's view-mode switch, CC-49355).
  //
  // CC-53132: the mount run now waits for the permission read, so nothing is
  // fetched under an unverified permission. Deliberately a `return` rather than
  // calling `runReport()` and letting it set its refusal message: an unresolved
  // read on MOUNT is the normal case for a few hundred ms, and flashing "we
  // couldn’t confirm your access" every time the page opens would train people
  // to ignore it. A read that stays unresolved surfaces as the panel below (and
  // the message above is what a deliberate Run click gets).
  //
  // Spec 131: deferred and cancellable, so an effect pass torn down before the
  // timer fires (StrictMode's simulated remount, when the permission read is
  // already cached) starts no run.
  useEffect(() => {
    if (!canAccessReady) return undefined
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [canAccessReady, canAccess, filters.budgetType])  // eslint-disable-line react-hooks/exhaustive-deps

  const isMatterMode = filters.budgetType !== 'timekeeper'
  // CC-50013 G2: shared metadata-driven bar (see buildFields). Budget Type stays
  // an action-row control, not a filter: it flips the report's whole column set
  // and triggers its own re-run, so modelling it as a filter would imply it could
  // be combined with the others.
  //
  // CC-50148: the queue spec uses the GET-replay job shape (`kind: 'accounting'`)
  // rather than the POST one, because this report runs as
  // `GET /reports/matter-metrics/report?…`. That is not cosmetic: the POST shape
  // derives a job's dedupe key from `endpoint.body`, and a GET has no body — so
  // every Matter Metrics run would have deduped to the SAME key regardless of its
  // filters, and a second run with a different date range would have been handed
  // the first one's result. The GET shape takes an explicit `filters` value as the
  // dedupe input instead. `matter-metrics` is registered in cb-api's
  // PERUSER_ACCOUNTING allowlist, so results are per-user and never shared.
  const fields = useMemo(() => buildFields(isMatterMode, practiceAreaOptions), [isMatterMode, practiceAreaOptions])
  const filterBar = (
    <SpecializedFilterBar
      fields={fields}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      disabled={!filters.startDate}
      runLabel={'Filter'}
      queue={reportId ? {
        reportId,
        name,
        kind: 'accounting',
        accountingKey: 'matter-metrics',
        endpoint: (f) => accountingEndpoint('/reports/matter-metrics/report', toWire(f)),
        filters: (f) => toWire(f),
        // Spec 200: an emailed copy carries CB's own export for the run's query.
        attachFormats: accountingAttachFormats('/reports/matter-metrics/report'),
      } : undefined}
      actions={(
        <>
          <span className="flex items-center gap-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Budget Type
            {[['matter', 'Matter'], ['timekeeper', 'Timekeeper']].map(([id, label]) => (
              <label key={id} className="flex items-center gap-1.5 text-sm font-normal normal-case cursor-pointer">
                <input
                  type="radio"
                  name="mm-budget-type"
                  value={id}
                  checked={filters.budgetType === id}
                  onChange={() => setFilters((f) => ({ ...f, budgetType: id }))}
                />
                {label}
              </label>
            ))}
          </span>
          <ReportViewsMenu
            storageKey={'report:matter-metrics'}
            currentFilters={filters}
            onApply={view.applyView}
            getPageState={view.getPageState}
          />
        </>
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} savedAsOf={savedAsOf}
    >
      {/* CC-53132: the permission read did not resolve, so the report was not
          run. Neither of the two things the page could otherwise show is true —
          it is not a refusal, and it is certainly not a report that came back
          with no matters in it. Say which one it is and offer the retry. */}
      {canAccessUnresolved && (
        <div role="alert" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">
            {canAccessState === 'blocked'
              ? 'Can’t reach the server, so we couldn’t confirm your access to Matter Metrics.'
              : canAccessState === 'failed'
                ? `We couldn’t confirm your access to Matter Metrics: ${canAccessQuery.error?.message || 'the check failed'}`
                : 'Your access to Matter Metrics has not been checked.'}
          </p>
          <p className="mt-1 text-amber-800">
            Nothing was run and nothing was refused — no figures on this page, and none missing
            from it, are CB’s answer. Try again once the connection is back.
          </p>
          <button
            type="button"
            onClick={() => canAccessQuery.refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      )}

      {/* CC-53132: the Practice Area picker is a matter-mode filter only, and an
          empty option list here is worse than a missing control. `practiceAreaWire('')`
          sends '', which CB reads as EVERY practice area — so a list that failed
          to load is indistinguishable from a filter the user chose to leave off,
          while the report covers the whole firm. Warned rather than blocked: the
          firm-wide run is the page's own default, so refusing to run would take
          away a report the user can legitimately have. */}
      {isMatterMode && practiceAreasUnresolved && (
        <div role="alert" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">
            {practiceAreasState === 'blocked'
              ? 'Can’t reach the server, so the Practice Area list didn’t load.'
              : practiceAreasState === 'failed'
                ? `The Practice Area list couldn’t be loaded: ${practiceAreasQuery.error?.message || 'the read failed'}`
                : 'The Practice Area list hasn’t been loaded.'}
          </p>
          <p className="mt-1 text-amber-800">
            This is not a firm with no practice areas. Until it loads you cannot narrow by one, and
            a run covers EVERY practice area rather than none — check the figures are the scope you
            wanted.
          </p>
          <button
            type="button"
            onClick={() => practiceAreasQuery.refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      )}

      {/* CC-50238: CB's own export already lays out the period / Actual-Budget
          matrix and the Totals row, so this renders it through the shared
          CSV adapter — which also gives the report sort, per-column filter,
          column show/hide, resize and the server-totals strip.

          CC-53132: only mounted once a run has RESOLVED. The grid's empty state
          and its row count are both claims about what CB returned, so neither may
          be drawn before there is an answer to describe — the shell's spinner
          covers the run itself, and the panels above cover a run that never
          started. */}
      {data ? (
        <CsvReportGrid key={view.gridKey} data={data} storageKey="report:matter-metrics" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
      ) : null}
    </SpecializedReportShell>
  )
}

export default MatterMetricsReport
